import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { collectTopicsUnder } from '../../courses/preview/TopicPreviewPanel';
import * as coursesApi from '../../courses/api';
import * as exerciseApi from '../../exercise-templates/api';
import { getNotesForWorkspace } from './notesApi';
import { getMarkersForWorkspace } from './blockMarkersApi';
import { getLessonHomeworksBulk } from '../lessonHomeworkApi';
import wsManager from '../../../services/wsManager';

const TOPIC_ELEMENT_BASE = '/api/topicelements';
const TOPIC_CONTAINER_BASE = '/api/topiccontainers';

function noteTargetKey(kind, id) {
   return kind + ':' + id;
}

// Groups a flat GET /api/studentelementnotes response by whichever ONE of
// the three target relations is set - nested full objects here (this
// backend has no @JsonSerialize customizations, only @JsonDeserialize, so
// every relation on a GET comes back as a full object, not a bare id -
// confirmed against the entity source, not guessed).
function groupNotesByTarget(notes) {
   const map = {};
   notes.forEach(note => {
      let key = null;
      if (note.topicElement) key = noteTargetKey('topicElement', note.topicElement.id);
      else if (note.exerciseElement) key = noteTargetKey('exerciseElement', note.exerciseElement.id);
      else if (note.exerciseInteractiveElement) key = noteTargetKey('exerciseInteractiveElement', note.exerciseInteractiveElement.id);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(note);
   });
   return map;
}

// Same idea as groupNotesByTarget, but a marker's target set is
// topicElement/topicContainer/topicExerciseTemplate (containers ARE a valid
// target here, unlike notes - see BlockMarker's own backend comment), and
// at most ONE marker exists per target, so this maps straight to the
// marker object rather than an array.
function groupMarkersByTarget(markers) {
   const map = {};
   markers.forEach(marker => {
      let key = null;
      if (marker.topicElement) key = noteTargetKey('topicElement', marker.topicElement.id);
      else if (marker.topicContainer) key = noteTargetKey('topicContainer', marker.topicContainer.id);
      else if (marker.topicExerciseTemplate) key = noteTargetKey('topicExerciseTemplate', marker.topicExerciseTemplate.id);
      if (!key) return;
      map[key] = marker;
   });
   return map;
}

// One linked exercise's own blocks - same 3 calls ExercisePreviewList.jsx
// makes, just eager-fetched up front here instead of per-component, since
// this whole page's point is showing everything about the student's course
// activity at once, not a lazily-scrolled admin editor.
function fetchExerciseContent(exerciseTemplateId) {
   return Promise.all([
      exerciseApi.getElements(exerciseTemplateId),
      exerciseApi.getInteractiveElements(exerciseTemplateId),
      exerciseApi.getContainers(exerciseTemplateId),
   ]).then(([elementsRes, interactiveRes, containersRes]) => ({
      elements: Array.isArray(elementsRes.data) ? elementsRes.data : [],
      interactiveElements: Array.isArray(interactiveRes.data) ? interactiveRes.data : [],
      containers: Array.isArray(containersRes.data) ? containersRes.data : [],
   }));
}

// One topic's own content (blocks/containers/linked exercises, each linked
// exercise's own content resolved too) - mirrors TopicPreviewSection's
// fetch in TopicPreviewPanel.jsx, plus resolving every linked exercise's
// content eagerly (that component only renders the exercise's name/blocks
// via a lazy child, ExercisePreviewList - this hook needs the ids up front
// to know what to fetch activity/notes for).
function fetchTopicContent(topic) {
   return Promise.all([
      coursesApi.getList(TOPIC_ELEMENT_BASE, 'topic', topic.id),
      coursesApi.getList(TOPIC_CONTAINER_BASE, 'topic', topic.id),
      coursesApi.getTopicExerciseTemplates(topic.id),
   ]).then(([elementsRes, containersRes, linksRes]) => {
      const elements = Array.isArray(elementsRes.data) ? elementsRes.data : [];
      const containers = Array.isArray(containersRes.data) ? containersRes.data : [];
      const links = Array.isArray(linksRes.data) ? linksRes.data : [];
      return Promise.all(links.map(link => (
         link.exerciseTemplate
            ? fetchExerciseContent(link.exerciseTemplate.id).then(content => ({ link, ...content }))
            : Promise.resolve(null)
      ))).then(exerciseLinks => ({
         topic, elements, containers,
         exerciseLinks: exerciseLinks.filter(Boolean),
      }));
   });
}

// Which of this student's exercises are ALSO lesson homework, keyed by
// ExerciseTemplate.id - the same id `exercise.link.exerciseTemplate.id`
// carries in WorkspaceTopicSection, so a plain lookup is all a consumer
// needs to highlight a match. Mirrors Events.jsx's own homework fetch
// (same /api/events?student= -> lesson ids -> getLessonHomeworksBulk
// pipeline, same `!hw.exerciseTemplate` filter) - this view just needed
// its own copy since it has no `events` list of its own to piggyback on.
// Fetched once per workspace load, not kept live - a teacher assigning new
// homework while this exact page is already open is rare enough that a
// page reload picking it up is fine (same tradeoff `topics` itself makes).
function fetchHomeworkMap(studentId) {
   return axios.get('/api/events?student=' + studentId, { withCredentials: true })
      .then(eventsRes => {
         const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];
         const lessonIds = Array.from(new Set(events.map(e => e.lesson && e.lesson.id).filter(Boolean)));
         if (lessonIds.length === 0) return {};
         return getLessonHomeworksBulk(lessonIds).then(hwRes => {
            const map = {};
            (Array.isArray(hwRes.data) ? hwRes.data : []).forEach(hw => {
               if (!hw.exerciseTemplate) return;
               map[hw.exerciseTemplate.id] = hw;
            });
            return map;
         });
      }).catch(() => ({}));
}

const EMPTY_STATS = { EXERCISE_ANSWER: 0, CONVERSATION_STEP: 0, NOTE_ADDED: 0 };

// The "what did the student do" slice - saved answer states, conversation
// progress+steps, and the activity-log counts - kept separate from course
// STRUCTURE (topics/blocks/containers, fetched once and never re-fetched
// live) since that's the part a live "answer_saved" WS event needs to
// refresh, and re-walking the whole course tree on every such event would
// be wasteful and pointless (structure doesn't change from a student
// answering something).
function fetchActivitySlice(workspace, studentId, topics) {
   return Promise.all([
      axios.get('/api/studentactivitylogs?workspace=' + workspace.id, { withCredentials: true }),
      axios.get('/api/studentconversationprogresses?workspace=' + workspace.id, { withCredentials: true }),
      axios.get('/api/exercisestudentsavedinteractiveelements?student=' + studentId, { withCredentials: true }),
   ]).then(([logsRes, progressesRes, savedStatesRes]) => {
      const interactiveElementIds = new Set();
      topics.forEach(t => t.exerciseLinks.forEach(ex => {
         ex.interactiveElements.forEach(el => interactiveElementIds.add(el.id));
      }));

      const savedStates = (Array.isArray(savedStatesRes.data) ? savedStatesRes.data : [])
         .filter(s => s.interactiveElement && interactiveElementIds.has(s.interactiveElement.id));
      const savedStateByElementId = {};
      savedStates.forEach(s => { savedStateByElementId[s.interactiveElement.id] = s; });

      const progresses = Array.isArray(progressesRes.data) ? progressesRes.data : [];
      return Promise.all(progresses.map(progress => (
         axios.get('/api/studentconversationprogresses/' + progress.id + '/steps', { withCredentials: true })
            .then(stepsRes => ({ progress, steps: Array.isArray(stepsRes.data) ? stepsRes.data : [] }))
      ))).then(progressWithSteps => {
         const progressByElementId = {};
         progressWithSteps.forEach(p => {
            if (p.progress.interactiveElement) progressByElementId[p.progress.interactiveElement.id] = p;
         });

         const logs = Array.isArray(logsRes.data) ? logsRes.data : [];
         const activityStats = { ...EMPTY_STATS };
         logs.forEach(l => {
            if (activityStats[l.activityType] != null) activityStats[l.activityType] += 1;
         });

         return { savedStateByElementId, progressByElementId, activityStats };
      });
   });
}

// Defensive check before applying a live WS event to this workspace's state
// - the topic name alone (`/topic/workspace/{id}`) SHOULD already guarantee
// only this workspace's events arrive here, but a server-side bug picking
// the wrong id could still send something to the wrong topic. Rather than
// trust the topic blindly, cross-check the payload's own workspace id
// (nested wherever this event type carries one) against what's actually
// open - a mismatch is silently dropped (logged, not applied), never
// merged into the view. Belt-and-suspenders, not a replacement for correct
// server-side topic naming.
function payloadBelongsToWorkspace(payload, workspaceId) {
   if (!payload) return false;
   const candidateWorkspace = payload.workspace || (payload.progress && payload.progress.workspace);
   if (candidateWorkspace && candidateWorkspace.id != null) {
      return String(candidateWorkspace.id) === String(workspaceId);
   }
   // Some payloads (e.g. a bare {id} delete confirmation) carry no
   // workspace reference at all - nothing to cross-check against, so allow
   // it through rather than silently never refreshing on deletes.
   return true;
}

export default function useStudentWorkspaceData(studentId, courseId) {
   const [state, setState] = useState({ status: 'loading' });

   // Cheap, targeted refetches (notes-only / activity-only) rather than
   // re-running the whole heavy structure fetch - used both for manual
   // triggers (after a teacher add/edit/delete in NotePin.jsx) and by the
   // live WS handler below (via the *Ref mirrors, so the handler - set up
   // once per workspace, not per render - always calls the LATEST version
   // instead of one closed over a stale `state` from subscribe time).
   const refreshNotes = () => {
      if (state.status !== 'ready') return;
      getNotesForWorkspace(state.workspace.id).then(notesRes => {
         const notes = Array.isArray(notesRes.data) ? notesRes.data : [];
         setState(current => (current.status === 'ready'
            ? { ...current, notesByTarget: groupNotesByTarget(notes), lastLiveUpdateAt: Date.now() }
            : current));
      }).catch(() => {});
   };
   const refreshNotesRef = useRef(refreshNotes);
   refreshNotesRef.current = refreshNotes;

   const refreshMarkers = () => {
      if (state.status !== 'ready') return;
      getMarkersForWorkspace(state.workspace.id).then(markersRes => {
         const markers = Array.isArray(markersRes.data) ? markersRes.data : [];
         setState(current => (current.status === 'ready'
            ? { ...current, markersByTarget: groupMarkersByTarget(markers), lastLiveUpdateAt: Date.now() }
            : current));
      }).catch(() => {});
   };
   const refreshMarkersRef = useRef(refreshMarkers);
   refreshMarkersRef.current = refreshMarkers;

   // Same idea, for the activity slice (after a live "answer_saved" WS
   // event) - course structure (topics/blocks) is never re-fetched here,
   // only saved-states/progress/activity-log counts.
   const refreshActivity = () => {
      if (state.status !== 'ready') return;
      fetchActivitySlice(state.workspace, studentId, state.topics).then(slice => {
         setState(current => (current.status === 'ready'
            ? { ...current, ...slice, lastLiveUpdateAt: Date.now() }
            : current));
      }).catch(() => {});
   };
   const refreshActivityRef = useRef(refreshActivity);
   refreshActivityRef.current = refreshActivity;

   const workspaceIdRef = useRef(null);
   workspaceIdRef.current = state.status === 'ready' ? state.workspace.id : null;

   useEffect(() => {
      if (!studentId || !courseId) return;
      let cancelled = false;
      setState(prev => (prev.status === 'ready' ? prev : { status: 'loading' }));

      axios.get('/api/studentcourseworkspaces?student=' + studentId, { withCredentials: true })
         .then(response => {
            const list = Array.isArray(response.data) ? response.data : [];
            const workspace = list.find(w => w.course && String(w.course.id) === String(courseId));
            if (!workspace) {
               if (!cancelled) setState({ status: 'not-started' });
               return null;
            }
            return workspace;
         })
         .then(workspace => {
            if (!workspace || cancelled) return;
            const course = workspace.course;

            return Promise.all([
               collectTopicsUnder('course', courseId),
               getNotesForWorkspace(workspace.id),
               // Tolerate /api/blockmarkers not existing yet (backend rollout
               // is separate from this frontend change) - a 404/500 here
               // must never take down the whole workspace view the way an
               // unhandled Promise.all rejection would.
               getMarkersForWorkspace(workspace.id).catch(() => ({ data: [] })),
               fetchHomeworkMap(studentId),
            ]).then(([topicList, notesRes, markersRes, homeworkByExerciseTemplateId]) => {
               if (cancelled) return;

               return Promise.all(topicList.map(fetchTopicContent)).then(topics => {
                  if (cancelled) return;

                  return fetchActivitySlice(workspace, studentId, topics).then(slice => {
                     if (cancelled) return;

                     const notes = Array.isArray(notesRes.data) ? notesRes.data : [];
                     const markers = Array.isArray(markersRes.data) ? markersRes.data : [];

                     setState({
                        status: 'ready',
                        workspace,
                        course,
                        topics,
                        ...slice,
                        notesByTarget: groupNotesByTarget(notes),
                        markersByTarget: groupMarkersByTarget(markers),
                        homeworkByExerciseTemplateId,
                        lastLiveUpdateAt: null,
                     });
                  });
               });
            });
         })
         .catch(() => { if (!cancelled) setState({ status: 'error' }); });

      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [studentId, courseId]);

   // Live updates: subscribe to this workspace's own WS topic once it's
   // known, dispatch on event.type, always re-verify the payload actually
   // belongs to THIS workspace before touching state (see
   // payloadBelongsToWorkspace's own comment - defense in depth,
   // independent of whether the backend's topic naming is itself correct).
   useEffect(() => {
      if (state.status !== 'ready') return undefined;
      const workspaceId = state.workspace.id;
      const topic = '/topic/workspace/' + workspaceId;

      const handler = (event) => {
         // workspaceIdRef, not the `workspaceId` closed over at subscribe
         // time, in case this same effect instance somehow outlives a
         // workspace change (it shouldn't, given the dependency array
         // below, but costs nothing to read the live value).
         if (!payloadBelongsToWorkspace(event && event.payload, workspaceIdRef.current)) {
            console.warn('Ignoring workspace WS event for a different workspace', event);
            return;
         }
         // answer_saved covers exercisestates for every gradeable type
         // AND embed_grid games - both save through the same generic
         // PlatformExerciseStateController endpoint (it doesn't branch on
         // element type), so there's no separate "game_saved" event to
         // handle. conversation_step is embed_conversation's own dedicated
         // save path (PlatformConversationStateController) - previously it
         // published nothing at all, so conversation progress never updated
         // live here even though the topic/handler plumbing already existed.
         if (event.type === 'answer_saved' || event.type === 'conversation_step') {
            refreshActivityRef.current();
         } else if (event.type === 'note_added' || event.type === 'note_updated' || event.type === 'note_deleted') {
            refreshNotesRef.current();
         } else if (event.type === 'marker_added' || event.type === 'marker_updated' || event.type === 'marker_deleted') {
            refreshMarkersRef.current();
         }
      };

      wsManager.subscribe(topic, handler);
      return () => { wsManager.unsubscribe(topic, handler); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.status === 'ready' ? state.workspace.id : null]);

   // Presence: "is the student's platform tab actually open on this course
   // right now" - a deliberately separate topic pair from the activity
   // channel above (WorkspacePresenceListener.java), since it's a
   // state-toggle (stays on/off) rather than a point-in-time event.
   // Subscribing alone triggers the backend to immediately push back the
   // CURRENT state, so no separate REST call is needed to learn the
   // initial value - null here just means "haven't heard yet" (briefly,
   // right after subscribing).
   const [studentOnline, setStudentOnline] = useState(null);
   useEffect(() => {
      if (state.status !== 'ready') return undefined;
      setStudentOnline(null);
      const topic = '/topic/workspace/' + state.workspace.id + '/presence';
      const handler = (event) => {
         if (event.type === 'presence_online') setStudentOnline(true);
         else if (event.type === 'presence_offline') setStudentOnline(false);
      };
      wsManager.subscribe(topic, handler);
      return () => { wsManager.unsubscribe(topic, handler); };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [state.status === 'ready' ? state.workspace.id : null]);

   return { ...state, refreshNotes, refreshMarkers, refreshActivity, studentOnline };
}
